import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject } from '@angular/core';
import { interval, Subscription, timer, throwError } from 'rxjs';
import { finalize, distinctUntilChanged, retry } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';

import { MutationService } from 'src/app/core/services/mutation.service';
import { FilesService } from 'src/app/core/services/files.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ToastService } from 'src/app/shared/utils/toast.service';

import { MutationStatusResponse, MutationProcessState, IMutation, getDisplayState } from 'src/app/core/domain/mutation';
import { IPatientResponseDTO } from 'src/app/core/domain/patient';
import { PatientLifecycleState } from 'src/app/core/domain/api-enums';

import { DiagnosisBadgesComponent } from 'src/app/shared/components/diagnosis-badges/diagnosis-badges.component';
import { DocumentScannerComponent } from 'src/app/shared/components/document-scanner/document-scanner.component';

@Component({
  selector: 'app-diagnosis-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    DiagnosisBadgesComponent
  ],
  templateUrl: './diagnosis-tab.component.html',
  styleUrls: ['./diagnosis-tab.component.scss']
})
export class DiagnosisTabComponent implements OnInit, OnDestroy {
  @Input() patient: IPatientResponseDTO;
  @Output() fileStateChange = new EventEmitter<boolean>();
  @Output() patientRefreshNeeded = new EventEmitter<void>();

  // Data
  mutations: IMutation[] = [];
  mutationStatus: MutationStatusResponse | null = null;

  // Pre-Processing Flag & Polling Subscription
  preProcessing: boolean = false;
  preProcessingPollingSubscription: Subscription | null = null;

  // Polling configuration (in milliseconds)
  preProcessingInterval: number = 5000; // Poll every 5 seconds
  maxPreProcessingPollingTime: number = 30000; // Maximum polling time: 30 seconds
  // UI Flags (we'll update these based on our flags and data)
  showSpinner: boolean = false;
  status_message: string = '';
  allow_upload: boolean = false;
  show_instructions: boolean = false;
  show_status: boolean = false;
  mutation_status: string = '';
  disableButton: boolean = false;

  // Services
  private mutationService = inject(MutationService);
  private filesService = inject(FilesService);
  private loadingService = inject(LoadingService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);

  // Current language
  currentLang: string = this.translate.currentLang || 'en';

  // Lifecycle Hooks
  ngOnInit(): void {
    this.mutations = this.patient?.mutations || [];
    this.initializeFromPatient();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.preProcessingPollingSubscription) {
      this.preProcessingPollingSubscription.unsubscribe();
      this.preProcessingPollingSubscription = null;
    }
  }

  // ------------------------------------------
  //  File Upload & Pre-Processing Polling
  // ------------------------------------------

  onFileMutationSelected(event: any): void {
    const file: File = event.target.files[0];
    if (!file) return;
    if (!this.isValidFile(file)) return;

    this.loadingService.show();

    this.filesService.getFileUploadToken(this.patient.rettxid, file.name, 'genetic-report')
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (response) => {
          // Upload the file
          this.filesService.uploadFile(response.file_url, file)
            .pipe(finalize(() => this.loadingService.hide()))
            .subscribe({
              next: () => {
                // Immediately set preProcessing flag to true and update UI
                this.preProcessing = true;
                this.updateUIFlags();

                // Start polling the backend during pre-processing
                this.startPreProcessingPolling();
              },
              error: (err) => {
                console.error('Upload failed', err);
              }
            });
        },
        error: (err) => {
          console.error('Error getting upload token', err);
        }
      });
  }

  private startPreProcessingPolling(): void {
    let elapsedTime = 0;
  
    this.preProcessingPollingSubscription = interval(this.preProcessingInterval).subscribe(() => {
      elapsedTime += this.preProcessingInterval;
  
      this.mutationService.getMutationStatus(this.patient.rettxid).subscribe({
        next: (status) => {
          const state = status.state;
          this.mutationStatus = status;
  
          if (state === 'PENDING_VALIDATION') {
            // Backend has registered the file, we can stop polling
            this.preProcessing = false;
            this.updateUIFlags();
            this.stopPreProcessingPolling();
            
            // Emit event to refresh patient data (lifecycle/capabilities may have changed)
            this.patientRefreshNeeded.emit();
          } else if (elapsedTime >= this.maxPreProcessingPollingTime) {
            // Timed out waiting for the file to appear
            this.preProcessing = false;
            this.toastService.showToast(
              'The file was uploaded, but it’s taking longer than expected to register in the system.',
              'error'
            );
            this.updateUIFlags();
            this.stopPreProcessingPolling();
          }
        },
        error: (err) => {
          console.error('Error during pre-processing polling', err);
        }
      });
    });
  }
  
  private stopPreProcessingPolling(): void {
    if (this.preProcessingPollingSubscription) {
      this.preProcessingPollingSubscription.unsubscribe();
      this.preProcessingPollingSubscription = null;
    }
  }
  
  // ------------------------------------------
  //  Initialize from Patient v2 API data
  // ------------------------------------------

  /**
   * Initializes mutation status from patient v2 API enrichment data.
   * This eliminates the need for a separate getMutationStatus() API call.
   * The backend now provides lifecycle, capabilities, and mutation_summary
   * directly in the patient response.
   */
  private initializeFromPatient(): void {
    const lifecycle = this.patient?.lifecycle;
    const capabilities = this.patient?.capabilities;
    const mutationSummary = this.patient?.mutation_summary;
    
    // Map lifecycle state to MutationProcessState
    const lifecycleToMutationState: Record<string, MutationProcessState> = {
      [PatientLifecycleState.DRAFT]: MutationProcessState.NO_FILE,
      [PatientLifecycleState.UNDER_REVIEW]: MutationProcessState.PENDING_VALIDATION,
      [PatientLifecycleState.MORE_INFO_REQUIRED]: MutationProcessState.MORE_INFO_REQUIRED,
      [PatientLifecycleState.CONFIRMED]: MutationProcessState.VALID_MUTATION,
      [PatientLifecycleState.LOCKED]: MutationProcessState.VALID_MUTATION
    };
    
    // Determine state from lifecycle, defaulting based on patient data
    let state: MutationProcessState;
    if (lifecycle?.state && lifecycleToMutationState[lifecycle.state]) {
      state = lifecycleToMutationState[lifecycle.state];
    } else if (this.patient?.main_diagnosis) {
      // Has diagnosis but no lifecycle state (legacy data) - assume confirmed
      state = MutationProcessState.VALID_MUTATION;
    } else if (this.mutations.length > 0) {
      // Has mutations but no lifecycle (legacy) - assume valid
      state = MutationProcessState.VALID_MUTATION;
    } else {
      // No lifecycle, no diagnosis, no mutations - draft/no file
      state = MutationProcessState.NO_FILE;
    }
    
    // Determine UI states based on lifecycle
    const isDraft = lifecycle?.state === PatientLifecycleState.DRAFT || state === MutationProcessState.NO_FILE;
    const isMoreInfoRequired = lifecycle?.state === PatientLifecycleState.MORE_INFO_REQUIRED;
    const isUnderReview = lifecycle?.state === PatientLifecycleState.UNDER_REVIEW;
    const isCompleted = lifecycle?.state === PatientLifecycleState.CONFIRMED || 
                        lifecycle?.state === PatientLifecycleState.LOCKED ||
                        state === MutationProcessState.VALID_MUTATION;
    
    // Determine upload permission:
    // - Use capabilities.upload_diagnosis if available (authoritative from backend)
    // - Otherwise: allow for DRAFT and MORE_INFO_REQUIRED, NOT for CONFIRMED/LOCKED/UNDER_REVIEW
    const canUpload = capabilities?.upload_diagnosis?.allowed ?? (isDraft || isMoreInfoRequired);
    
    // Determine what to show:
    // - show_instructions: Only for DRAFT with no diagnosis (first-time upload guidance)
    // - show_status: For all non-draft states (shows current validation status)
    // - For CONFIRMED/LOCKED: Show completed status, NO upload buttons
    // - For MORE_INFO_REQUIRED: Show status message AND upload buttons
    
    this.mutationStatus = {
      state: state,
      validation_status: mutationSummary?.validation?.status_code || null,
      allow_upload: canUpload && !isCompleted, // Never allow upload for completed patients
      show_instructions: isDraft && !this.patient?.main_diagnosis && !this.mutations.length,
      show_status: !isDraft, // Show status card for all non-draft states
      show_mutation_list: this.mutations.length > 0,
      last_file_id: '',
      last_updated_at: lifecycle?.updated_at || '',
      // Use localized description if available, fallback to regular description
      validation_description: mutationSummary?.validation?.description_localized || 
                              mutationSummary?.validation?.description || ''
    };
    
    this.updateUIFlags();
  }

  // ------------------------------------------
  //  Mutation Status Polling (for file uploads only)
  // ------------------------------------------

  /**
   * @deprecated Use initializeFromPatient() for initial state.
   * This method is kept only for polling after file uploads to detect
   * when the backend has processed a newly uploaded file.
   * TODO: Consider emitting an event to refresh parent patient data instead.
   */
  private checkMutationStatus(): void {
    if (!this.patient?.rettxid) return;
    this.mutationService.getMutationStatus(this.patient.rettxid)
      .pipe(
        // Retry once with 500ms delay on 401 errors (auth token race condition)
        retry({
          count: 1,
          delay: (error) => error.status === 401 ? timer(500) : throwError(() => error)
        }),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe({
        next: (status) => {
          this.mutationStatus = status;
          // If there are pending files, update the UI (this block may not be used during preProcessing)
          this.updateUIFlags();
        },
        error: (err) => {
          console.error('Error fetching mutation status:', err);
          this.mutationStatus = null;
          this.updateUIFlags();
        }
      });
  }

  // Helper to map state to a CSS class for color indication
  getStatusClass(state: string): string {
    switch (state) {
      case MutationProcessState.PENDING_VALIDATION:
        return 'status-amber'; // amber for pending
      case MutationProcessState.VALID_MUTATION:
        return 'status-green'; // green for valid/success
      case MutationProcessState.VALIDATION_ERROR:
      case MutationProcessState.NO_MUTATION_FOUND:
        return 'status-red';   // red for invalid/error
      case MutationProcessState.CLINICAL_DIAGNOSIS:
        return 'status-green';  // green for clinical diagnosis
      case MutationProcessState.MORE_INFO_REQUIRED:
      case MutationProcessState.MISSING_GENETIC_REPORT:
      case MutationProcessState.NO_RETT_DIAGNOSIS:
      case MutationProcessState.MISSING_MUTATION_IN_REPORT:
      case MutationProcessState.MISSING_NAME:
      case MutationProcessState.MISSING_SURNAME:
      case MutationProcessState.MISSING_DOB:
      case MutationProcessState.CUSTOM_INFO_REQUIRED:
      case MutationProcessState.INFO_MISMATCH_NAME:
      case MutationProcessState.INFO_MISMATCH_DOB:
        return 'status-amber'; // amber for more info required (all variants)
      default:
        return 'status-amber'; // safe default for unknown states
    }
  }

  // Helper to get the display state using validation_status if available
  getDisplayStatusState(): string {
    if (!this.mutationStatus) {
      return MutationProcessState.MORE_INFO_REQUIRED;
    }
    
    // Use getDisplayState to handle validation_status mapping
    return getDisplayState(this.mutationStatus);
  }

  // Helper to map state to a translation key for the status message
  getStatusLabel(state: string): string {
    switch (state) {
      case MutationProcessState.PENDING_VALIDATION:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.PENDING_VALIDATION_LONG';
      case MutationProcessState.VALIDATION_ERROR:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.VALIDATION_ERROR';
      case MutationProcessState.VALID_MUTATION:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.VALID_MUTATION';
      case MutationProcessState.NO_MUTATION_FOUND:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.NO_MUTATION_FOUND';
      case MutationProcessState.CLINICAL_DIAGNOSIS:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.CLINICAL_DIAGNOSIS';
      case MutationProcessState.MORE_INFO_REQUIRED:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MORE_INFO_REQUIRED';
      case MutationProcessState.MISSING_GENETIC_REPORT:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MISSING_GENETIC_REPORT';
      case MutationProcessState.NO_RETT_DIAGNOSIS:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.NO_RETT_DIAGNOSIS';
      case MutationProcessState.MISSING_MUTATION_IN_REPORT:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MISSING_MUTATION_IN_REPORT';
      case MutationProcessState.MISSING_NAME:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MISSING_NAME';
      case MutationProcessState.MISSING_SURNAME:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MISSING_SURNAME';
      case MutationProcessState.MISSING_DOB:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MISSING_DOB';
      case MutationProcessState.CUSTOM_INFO_REQUIRED:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.CUSTOM_INFO_REQUIRED';
      case MutationProcessState.INFO_MISMATCH_NAME:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.INFO_MISMATCH_NAME';
      case MutationProcessState.INFO_MISMATCH_DOB:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.INFO_MISMATCH_DOB';
      default:
        return 'PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.MORE_INFO_REQUIRED'; // fallback for unknown states
    }
  }

  // ------------------------------------------
  //  UI Flags Logic
  // ------------------------------------------

  private updateUIFlags(): void {
    requestAnimationFrame(() => {
      if (this.preProcessing) {
        // During pre-processing: only show the spinner; hide all other elements.
        this.showSpinner = true;
        this.show_instructions = false;
        this.show_status = false;
        this.allow_upload = false;
        this.status_message = '';
      } else {
        // After pre-processing, update based on the mutation status and data.
        this.showSpinner = false;
        this.allow_upload = this.mutationStatus?.allow_upload || false;
        this.show_instructions = this.mutationStatus?.show_instructions || false;
        this.show_status = this.mutationStatus?.show_status || false;
        this.status_message = this.mutationStatus?.state
          ? this.translate.instant(`PATIENT_DETAIL.CONTENT.MUTATIONS.STATUS.${this.mutationStatus.state}`)
          : '';
        
        // Emit file state: true if upload is needed, false otherwise
        // Upload is needed when show_instructions is true (no file yet)
        this.fileStateChange.emit(this.show_instructions);
      }
    });
  }

  // ------------------------------------------
  //  File Validation
  // ------------------------------------------

  private isValidFile(file: File): boolean {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (!validTypes.includes(file.type)) {
      this.toastService.showToast('Invalid file type.', 'error');
      return false;
    }
    if (file.size > maxSize) {
      this.toastService.showToast('File size exceeds the limit.', 'error');
      return false;
    }
    return true;
  }

  // Stop any regular polling if needed
  private stopPolling(): void {
    // Stop any subscriptions related to regular polling.
  }

  // Identify if the mutation status is in an error state
  isErrorState(state: string): boolean {
    return ([
      MutationProcessState.VALIDATION_ERROR,
      MutationProcessState.NO_MUTATION_FOUND,
      MutationProcessState.MORE_INFO_REQUIRED,
      MutationProcessState.MISSING_GENETIC_REPORT,
      MutationProcessState.NO_RETT_DIAGNOSIS,
      MutationProcessState.MISSING_MUTATION_IN_REPORT,
      MutationProcessState.MISSING_NAME,
      MutationProcessState.MISSING_SURNAME,
      MutationProcessState.MISSING_DOB,
      MutationProcessState.CUSTOM_INFO_REQUIRED,
      MutationProcessState.INFO_MISMATCH_NAME,
      MutationProcessState.INFO_MISMATCH_DOB
    ] as string[]).includes(state);
  }

  // ------------------------------------------
  //  Genetics action-card header state (passport reskin)
  // ------------------------------------------

  /** True only when a genetic report has been accepted and validated (green). */
  get isComplete(): boolean {
    return this.show_status
      && !this.allow_upload
      && !this.showSpinner
      && this.getStatusClass(this.getDisplayStatusState()) === 'status-green';
  }

  /** Translation key for the action-card header subtitle. */
  get headerSubtitleKey(): string {
    if (this.showSpinner) return 'PATIENT_DETAIL.HOME.GENETIC_PROCESSING';
    if (this.isComplete) return 'PATIENT_DETAIL.HOME.GENETIC_DONE';
    if (this.allow_upload || this.show_instructions) return 'PATIENT_DETAIL.HOME.GENETIC_PENDING';
    if (this.show_status) return 'PATIENT_DETAIL.HOME.GENETIC_REVIEW';
    return 'PATIENT_DETAIL.HOME.GENETIC_PENDING';
  }

  openDocumentScanner(): void {
    // Check for mobile device using both screen width and user agent
    const isMobile = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    const dialogRef = this.dialog.open(DocumentScannerComponent, {
      width: isMobile ? '100%' : '800px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100%' : 'auto',
      maxHeight: isMobile ? '100vh' : '90vh',
      panelClass: isMobile ? 'fullscreen-mobile-dialog' : 'desktop-dialog',
      backdropClass: isMobile ? 'mobile-backdrop' : '',
      disableClose: true, // prevent accidental closure
      position: isMobile ? { top: '0', left: '0', right: '0', bottom: '0' } : null,
      autoFocus: false, // Prevent auto focus which can cause issues on mobile
      restoreFocus: false // Don't restore focus after dialog closes (prevents scrolling issues on mobile)
    });
    
    dialogRef.afterClosed().subscribe((scannedFile: File) => {
      if (scannedFile) {
        this.uploadScannedDocument(scannedFile);
      }
    });
  }

  uploadScannedDocument(file: File): void {
    if (!this.isValidFile(file)) return;

    this.loadingService.show();

    this.filesService.getFileUploadToken(this.patient.rettxid, file.name, 'genetic-report')
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe({
        next: (response) => {
          // Upload the file
          this.filesService.uploadFile(response.file_url, file)
            .pipe(finalize(() => this.loadingService.hide()))
            .subscribe({
              next: () => {
                // Immediately set preProcessing flag to true and update UI
                this.preProcessing = true;
                this.updateUIFlags();

                // Start polling the backend during pre-processing
                this.startPreProcessingPolling();
              },
              error: (err) => {
                console.error('Upload failed', err);
              }
            });
        },
        error: (err) => {
          console.error('Error getting upload token', err);
        }
      });
  }
  
}
